'use client';
import { prependSlash } from '@repo/ui/src/components/Anchor/helpers';
import { OrderRowDetails } from '@hooks/order';
import { LinkProps } from '@repo/ui/src/types/link';
import OrderRowBody from '@views/Account/OrderRowBody';
import React from 'react';
import { format } from 'date-fns';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { useCurrencyString } from '@repo/ui/src/hooks/useCurrencyString';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { linkClicked } from '@repo/ui/src/services/analytics';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';

export interface OrderRowProps extends OrderRowDetails {
  order?: string;
  total?: string;
  date?: string;
  orderDetailsLink?: LinkProps;
  reviewLinkText?: string;
  quantityText?: string;
  giftCardLink: LinkProps;
  hideReviewLink?: boolean;
}

declare global {
  interface Window {
    $BV: {
      ui: (category: string, type: string, options: any) => void;
    };
  }
}

const DashboardOrderRow = ({
  currency,
  orderNo,
  creationDate,
  orderTotal,
  productItems,
  giftCertificateItems,
  order,
  total,
  date,
  orderDetailsLink,
  reviewLinkText = 'Write a Review',
  quantityText = 'Quantity',
  giftCardLink,
  hideReviewLink = false,
  url: externalLink,
}: OrderRowProps) => {
  const getCurrencyString = useCurrencyString(currency);
  const { locale } = useCurrentUrl();

  const link = React.useMemo(() => {
    if (!orderDetailsLink?.link) {
      return '';
    }
    const url = new URL(prependSlash(orderDetailsLink?.link), window.location.origin);
    url.searchParams.set('order', orderNo);
    return url.toString().replace(window.location.origin, '');
  }, [orderDetailsLink, orderNo]);

  return (
    <li
      className="bg-warm-100 border border-warm-400 px-4 py-5 mb-7 sm:p-10"
      data-testid={`order-row-${orderNo}`}
    >
      <div className="flex flex-col justify-between w-full md:flex-row">
        <div className="flex flex-col">
          <p className="tra-text-web-body-l font-bold">
            {order} #{orderNo}
          </p>
          <p className="tra-text-web-body-l">
            {total}: <span className="font-bold">{getCurrencyString(orderTotal)}</span>
          </p>
          <p className="tra-text-web-body-l">
            {date}:{' '}
            <span className="font-bold">
              {creationDate && format(new Date(creationDate), 'MMMM dd, yyyy')}
            </span>
          </p>
        </div>
        {orderDetailsLink && (
          <Button
            size="small"
            variant={ButtonVariant.secondary}
            className="max-h-10 mt-6 md:mt-0"
            as={Anchor}
            link={link}
            externalLink={externalLink ?? undefined}
            isExternal={!!externalLink}
            onClick={() => {
              linkClicked({
                affiliation: 'website',
                brand: 'Traeger',
                link_location: 'my account_order history',
                link_text: orderDetailsLink.displayName ?? '',
                link_type: 'Internal',
                link_url: link,
                locale,
              });
            }}
          >
            {orderDetailsLink.displayName}
          </Button>
        )}
      </div>
      <hr className="border-warm-400 my-3 border" />
      <OrderRowBody
        getCurrencyString={getCurrencyString}
        productItems={productItems}
        giftCertificateItems={giftCertificateItems}
        reviewLinkText={reviewLinkText}
        quantityText={quantityText}
        giftCardLink={giftCardLink}
        hideReviewLink={hideReviewLink}
        isAccountPage
      />
    </li>
  );
};

export default DashboardOrderRow;
